import { useState, useEffect, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SearchableTeam {
  externalId: string;
  name: string;
  abbreviation?: string;
  market?: string;
}

export interface LeadingOption {
  value: string;
  label: string;
}

interface SearchableTeamSelectProps {
  teams: SearchableTeam[];
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  /** Custom display name for each team (e.g. NBA full names) */
  getDisplayName?: (team: SearchableTeam) => string;
  /** Custom search text for filtering (default: name + abbreviation) */
  getSearchText?: (team: SearchableTeam) => string;
  /** Optional options at top (e.g. [{ value: 'all', label: 'All Teams' }]) */
  leadingOptions?: LeadingOption[];
  className?: string;
}

/**
 * A searchable team dropdown that filters by team name or team code (abbreviation).
 * Use in modals and forms where team lists can be long.
 */
export function SearchableTeamSelect({
  teams,
  value,
  onValueChange,
  disabled = false,
  placeholder = 'Select a team',
  id = 'team-select',
  getDisplayName,
  getSearchText,
  leadingOptions,
  className,
}: SearchableTeamSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim()) return teams;
    const q = searchQuery.toLowerCase().trim();
    return teams.filter(team => {
      const searchText = getSearchText
        ? getSearchText(team).toLowerCase()
        : `${team.name} ${team.abbreviation || ''}`.toLowerCase();
      return searchText.includes(q);
    });
  }, [teams, searchQuery, getSearchText]);

  const displayTeam = (team: SearchableTeam) => (getDisplayName ? getDisplayName(team) : team.name);

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectTrigger id={id} className={cn(className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[300px]">
        <div
          className="sticky top-0 z-10 bg-popover p-2 border-b"
          onPointerDown={e => e.preventDefault()}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by team name or code..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-8"
              onPointerDown={e => e.stopPropagation()}
            />
          </div>
        </div>
        {leadingOptions?.map(opt => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
        {filteredTeams.length === 0 && !leadingOptions?.length ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No teams found</div>
        ) : (
          filteredTeams.map(team => (
            <SelectItem key={team.externalId} value={team.externalId}>
              {displayTeam(team)}
              {team.abbreviation ? ` (${team.abbreviation})` : ''}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
